/**
 * The icons a folder may carry, in ONE place.
 *
 * The picker and the tree previously disagreed: the picker offered ten choices
 * and saved the lucide name, while the tree drew a hard-coded folder whatever
 * was saved. The caption under the picker says "Shown beside the folder when
 * techs browse", so picking an icon and seeing nothing change read as broken.
 *
 * The STORED VALUE is still the lucide name, unchanged, so the tech app reads
 * exactly what it read before.
 */
import {
  Droplet,
  Flame,
  Folder,
  FolderOpen,
  Hammer,
  Plug,
  Snowflake,
  Thermometer,
  Wind,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';

/** Offered by the picker, in this order. */
export const CATEGORY_ICON_CHOICES = [
  { name: 'folder', Icon: Folder },
  { name: 'wrench', Icon: Wrench },
  { name: 'hammer', Icon: Hammer },
  { name: 'snowflake', Icon: Snowflake },
  { name: 'flame', Icon: Flame },
  { name: 'droplet', Icon: Droplet },
  { name: 'wind', Icon: Wind },
  { name: 'plug', Icon: Plug },
  { name: 'zap', Icon: Zap },
  { name: 'thermometer', Icon: Thermometer },
] as const;

export type CategoryIconName = (typeof CATEGORY_ICON_CHOICES)[number]['name'];

const BY_NAME = new Map<string, LucideIcon>(
  CATEGORY_ICON_CHOICES.map(({ name, Icon }) => [name, Icon]),
);

/**
 * Which icon to draw beside a folder in the tree.
 *
 * `folder` and an unset icon both keep the open/closed pair, because that pair
 * carries real information — whether the folder is expanded. A folder that
 * chose a MEANING (a flame, a wrench) keeps that meaning in both states; it
 * would be odd for picking "flame" to yield a folder whenever the row expands.
 *
 * An unrecognised name falls back rather than throwing: the column is a plain
 * `String`, so the tech app or an import can put anything in it.
 */
export function categoryIcon(
  name: string | null | undefined,
  { isOpen = false }: { isOpen?: boolean } = {},
): LucideIcon {
  if (name && name !== 'folder') {
    const picked = BY_NAME.get(name);
    if (picked) return picked;
  }
  return isOpen ? FolderOpen : Folder;
}
